import { useState, type FormEvent } from 'react'
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material'
import { supabase } from '../lib/supabase'
import { useAuth } from '../state/AuthContext'

const MINIMO = 8

/**
 * Troca a senha de quem está logado — o caminho para sair da senha provisória. Obrigatório
 * (senha criada ou redefinida por outra pessoa): não fecha sem trocar, e sair é a única saída.
 */
export function TrocarSenhaDialog({ aberto, onFechar, obrigatorio = false }: { aberto: boolean; onFechar: () => void; obrigatorio?: boolean }) {
  const { senhaTrocada, logout } = useAuth()
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [pronto, setPronto] = useState(false)

  const fechar = () => {
    setSenha('')
    setConfirmacao('')
    setErro(null)
    setPronto(false)
    onFechar()
  }

  const salvar = async (e: FormEvent) => {
    e.preventDefault()
    if (senha.length < MINIMO) return setErro(`A senha precisa de pelo menos ${MINIMO} caracteres`)
    if (senha !== confirmacao) return setErro('As duas senhas não são iguais')
    setSalvando(true)
    const { error } = await supabase.auth.updateUser({ password: senha })
    setSalvando(false)
    if (error) {
      setErro(/different from the old/i.test(error.message) ? 'A nova senha precisa ser diferente da atual' : error.message)
    } else {
      await senhaTrocada()
      setPronto(true)
    }
  }

  return (
    <Dialog open={aberto} onClose={obrigatorio ? undefined : fechar} fullWidth maxWidth="xs">
      <form onSubmit={salvar}>
        <DialogTitle>{obrigatorio ? 'Crie a sua senha' : 'Trocar senha'}</DialogTitle>
        <DialogContent>
          {pronto ? (
            <Alert severity="success">Senha trocada. Use a nova no próximo login.</Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {obrigatorio ? (
                <Alert severity="info">
                  Você entrou com uma senha provisória, criada por outra pessoa. Antes de continuar, escolha uma senha só sua.
                </Alert>
              ) : null}
              {erro ? <Alert severity="error">{erro}</Alert> : null}
              <TextField
                autoFocus
                type="password"
                label="Nova senha"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                helperText={`Mínimo de ${MINIMO} caracteres`}
              />
              <TextField
                type="password"
                label="Repita a nova senha"
                autoComplete="new-password"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {obrigatorio && !pronto ? (
            <Button onClick={() => logout()}>Sair</Button>
          ) : (
            <Button onClick={fechar}>{pronto ? 'Fechar' : 'Cancelar'}</Button>
          )}
          {!pronto ? (
            <Button type="submit" variant="contained" disabled={salvando}>
              {salvando ? <CircularProgress size={20} color="inherit" /> : 'Trocar'}
            </Button>
          ) : null}
        </DialogActions>
      </form>
    </Dialog>
  )
}
